import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Clock, Monitor, RefreshCw, Search, Smartphone, UserCircle, X } from 'lucide-react'
import { useAuthStore } from '@/store'
import {
  fetchTeamActivityStats,
  fetchUserScreenLog,
  type ActivityStats,
  type ScreenLogEntry,
} from '@/services/employeeActivityService'

const LATEST_APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '2.4.1'

type Role =
  | 'management_company'
  | 'owner'
  | 'manager'
  | 'member'
  | 'territorial_manager'
  | 'personal_manager'
  | 'trainer'
  | 'user'
type RoleGroup = 'management_company' | 'owners' | 'team' | 'other'
type RiskLevel = 'ok' | 'warning' | 'critical' | 'empty'
type FilterRole = 'all' | Role

interface Employee {
  id: string
  name: string
  username: string
  email: string | null
  role: Role
  roleGroup: RoleGroup
  lastSeenAt: string | null
  lastSeenDaysAgo: number | null
  appVersion: string | null
  deviceType: 'iOS' | 'Android' | 'Web' | null
  sessions: [number, number, number, number, number]
  riskLevel: RiskLevel
  /** Часы за 4 недели по фактическому исполнителю. */
  hours4w: number
  isDeveloper: boolean
  /** Под сколькими чужими учётками работал этот человек и сколько часов. */
  workedAsCount: number
  workedAsHours: number
  /** Локальные сессии без имени исполнителя — в активность не входят. */
  devSessions: number
  devHours: number
}

const ROLE_LABELS: Record<Role, string> = {
  management_company: 'УК',
  owner: 'Владелец',
  manager: 'Менеджер',
  member: 'Участник',
  territorial_manager: 'Терр. менеджер',
  personal_manager: 'Перс. менеджер',
  trainer: 'Тренер',
  user: 'Пользователь',
}

const GROUP_LABELS: Record<RoleGroup, string> = {
  management_company: 'Управляющая компания',
  owners: 'Владельцы',
  team: 'Команды студий',
  other: 'Остальные пользователи',
}

const ROLE_OPTIONS: FilterRole[] = [
  'all',
  'management_company',
  'territorial_manager',
  'personal_manager',
  'trainer',
  'owner',
  'manager',
  'member',
  'user',
]

const CONTEXT_LABELS: Record<string, string> = {
  partner_number: 'Франчайзи',
  studio_id: 'Студия',
  city_id: 'Город',
  series_id: 'Серия',
  contract_id: 'Договор',
  target_user_id: 'Пользователь',
  section: 'Раздел',
}

function mapRole(dbRole: string | null | undefined): Role {
  if (dbRole === 'management_company') return 'management_company'
  if (dbRole === 'owner') return 'owner'
  if (dbRole === 'manager') return 'manager'
  if (dbRole === 'territorial_manager') return 'territorial_manager'
  if (dbRole === 'personal_manager') return 'personal_manager'
  if (dbRole === 'trainer') return 'trainer'
  if (dbRole === 'user') return 'user'
  return 'member'
}

function mapRoleGroup(dbRoleGroup: string | null | undefined, role: Role): RoleGroup {
  if (dbRoleGroup === 'management_company') return 'management_company'
  if (dbRoleGroup === 'owners') return 'owners'
  if (dbRoleGroup === 'team') return 'team'
  if (role === 'management_company' || role === 'territorial_manager' || role === 'personal_manager' || role === 'trainer') {
    return 'management_company'
  }
  if (role === 'owner') return 'owners'
  if (role === 'manager' || role === 'member') return 'team'
  return 'other'
}

function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
}

function computeRisk(lastSeenDaysAgo: number | null, sessions: number[]): RiskLevel {
  if (lastSeenDaysAgo === null && sessions.every(s => s === 0)) return 'empty'
  if (lastSeenDaysAgo === null) return 'empty'
  if (lastSeenDaysAgo > 14 || sessions[4] === 0) return 'critical'
  if (lastSeenDaysAgo > 7 || sessions[4] < 3) return 'warning'
  return 'ok'
}

function buildEmployee(stats: ActivityStats): Employee {
  const name =
    [stats.first_name, stats.last_name].filter(Boolean).join(' ') ||
    stats.tg_username ||
    stats.email ||
    'Без имени'
  const role = mapRole(stats.role)
  const sessions: [number, number, number, number, number] = [
    stats.w0_sessions ?? 0,
    stats.w1_sessions ?? 0,
    stats.w2_sessions ?? 0,
    stats.w3_sessions ?? 0,
    stats.w4_sessions ?? 0,
  ]
  const lastSeenDaysAgo = daysSince(stats.last_seen_at)
  return {
    id: stats.user_id,
    name,
    username: stats.tg_username ?? '',
    email: stats.email ?? null,
    role,
    roleGroup: mapRoleGroup(stats.role_group, role),
    lastSeenAt: stats.last_seen_at ?? null,
    lastSeenDaysAgo,
    appVersion: stats.app_version ?? null,
    deviceType: (stats.device_type as Employee['deviceType']) ?? null,
    sessions,
    riskLevel: computeRisk(lastSeenDaysAgo, sessions),
    // Поля разметки исполнителя приходят из миграции 20260923120000; до её
    // применения RPC их не отдаёт, и экран просто не показывает эти бейджи.
    hours4w: Number(stats.hours_4w ?? 0),
    isDeveloper: Boolean(stats.is_developer),
    workedAsCount: Number(stats.worked_as_count ?? 0),
    workedAsHours: Number(stats.worked_as_hours ?? 0),
    devSessions: Number(stats.dev_sessions ?? 0),
    devHours: Number(stats.dev_hours ?? 0),
  }
}

function formatHours(hours: number): string {
  if (!hours) return '0 ч'
  if (hours < 1) return `${Math.round(hours * 60)} мин`
  return `${hours.toFixed(1).replace('.', ',')} ч`
}

function pluralizeAccounts(count: number): string {
  const tail = count % 10
  const hundred = count % 100
  if (tail === 1 && hundred !== 11) return 'учётки'
  return 'учёток'
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Нет данных'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatTimeOnly(value: string | null | undefined): string {
  if (!value) return ''
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatEventInterval(entry: ScreenLogEntry): string {
  const start = entry.started_at
  const end = entry.created_at
  if (!start) return formatDateTime(end)
  const startDay = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(start))
  return `${startDay}, ${formatTimeOnly(start)}-${formatTimeOnly(end)}`
}

function formatDuration(sec: number | null | undefined): string {
  if (sec == null) return 'без длительности'
  if (sec < 60) return `${sec} сек`
  const min = Math.floor(sec / 60)
  const rest = sec % 60
  return rest > 0 ? `${min} мин ${rest} сек` : `${min} мин`
}

function formatLastSeen(emp: Employee): string {
  if (emp.lastSeenDaysAgo === null) return 'Нет событий'
  if (emp.lastSeenDaysAgo === 0) return 'Сегодня'
  if (emp.lastSeenDaysAgo === 1) return 'Вчера'
  return `${emp.lastSeenDaysAgo} дн. назад`
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'U'
}

function riskClasses(risk: RiskLevel): string {
  if (risk === 'critical') return 'bg-red-50 text-red-700 border-red-200'
  if (risk === 'warning') return 'bg-amber-50 text-amber-700 border-amber-200'
  if (risk === 'ok') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  return 'bg-slate-50 text-slate-500 border-slate-200'
}

function riskLabel(risk: RiskLevel): string {
  if (risk === 'critical') return 'Неактивен'
  if (risk === 'warning') return 'Низкая активность'
  if (risk === 'ok') return 'Активен'
  return 'Нет данных'
}

function fullPath(entry: ScreenLogEntry): string {
  if (!entry.pathname) return 'Адрес не записан'
  return `${entry.pathname}${entry.search ?? ''}`
}

function contextPairs(entry: ScreenLogEntry): Array<[string, string]> {
  const context = entry.route_context ?? {}
  const pairs: Array<[string, string]> = []
  Object.entries(context).forEach(([key, value]) => {
    if (key === 'detail_label' || key === 'entity_type' || key === 'query') return
    if (typeof value === 'string' && value) pairs.push([CONTEXT_LABELS[key] ?? key, value])
  })
  const query = context.query
  if (query && typeof query === 'object' && !Array.isArray(query)) {
    Object.entries(query as Record<string, unknown>).forEach(([key, value]) => {
      if (typeof value === 'string' && value) pairs.push([`query:${key}`, value])
    })
  }
  return pairs
}

function contextTitle(entry: ScreenLogEntry): string | null {
  const label = entry.route_context?.detail_label
  return typeof label === 'string' && label ? label : null
}

function RowSkeleton() {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
      <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-muted" />
    </div>
  )
}

function EmployeeRow({
  emp,
  selected,
  onClick,
}: {
  emp: Employee
  selected: boolean
  onClick: () => void
}) {
  const isOutdatedVersion = emp.appVersion && emp.appVersion !== LATEST_APP_VERSION
  return (
    <button
      type="button"
      onClick={onClick}
      className={`grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-border px-3 py-3 text-left transition-colors hover:bg-muted/40 ${
        selected ? 'bg-sky-50' : 'bg-card'
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-xs font-semibold text-slate-700">
            {initials(emp.name)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">{emp.name}</div>
            <div className="truncate text-xs text-muted-foreground">
              {emp.username ? `@${emp.username}` : emp.email ?? 'Без контакта'}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="rounded border border-border bg-background px-1.5 py-0.5 text-muted-foreground">
            {ROLE_LABELS[emp.role]}
          </span>
          <span className={`rounded border px-1.5 py-0.5 ${riskClasses(emp.riskLevel)}`}>
            {riskLabel(emp.riskLevel)}
          </span>
          {emp.isDeveloper && (
            <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-sky-700">
              разработка
            </span>
          )}
          {emp.workedAsCount > 0 && (
            <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-sky-700">
              под {emp.workedAsCount} чужими {pluralizeAccounts(emp.workedAsCount)}
            </span>
          )}
          {emp.devSessions > 0 && (
            <span
              className="rounded border border-border bg-muted px-1.5 py-0.5 text-muted-foreground"
              title="Локальная сборка без имени исполнителя. В часы активности не входит."
            >
              локально {formatHours(emp.devHours)}
            </span>
          )}
          {isOutdatedVersion && (
            <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-700">
              версия {emp.appVersion}
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-xs font-medium text-foreground">{formatLastSeen(emp)}</div>
        <div className="mt-1 text-[11px] text-muted-foreground">{emp.sessions[4]} сесс. за нед.</div>
        <div className="text-[11px] text-muted-foreground">{formatHours(emp.hours4w)} за 4 нед.</div>
      </div>
    </button>
  )
}

function ActivityEventRow({ entry }: { entry: ScreenLogEntry }) {
  const pairs = contextPairs(entry)
  const title = contextTitle(entry)
  return (
    <div className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Monitor className="h-4 w-4 shrink-0 text-slate-500" />
            <div className="truncate text-sm font-semibold text-foreground">{entry.screen_name}</div>
            {entry.acted_as_name ? (
              <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] text-sky-700">
                под учёткой {entry.acted_as_name}
              </span>
            ) : entry.is_dev_session ? (
              <span
                className="rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
                title="Событие пришло из локальной сборки"
              >
                локальная сборка
              </span>
            ) : null}
          </div>
          {title && <div className="mt-1 text-sm text-slate-700">{title}</div>}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-xs font-semibold text-foreground">{formatEventInterval(entry)}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{formatDuration(entry.duration_sec)}</div>
        </div>
      </div>

      {pairs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {pairs.map(([label, value]) => (
            <span key={`${label}-${value}`} className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
              {label}: {value}
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 break-all rounded bg-slate-50 px-2 py-1 font-mono text-[11px] leading-relaxed text-slate-600">
        {fullPath(entry)}
      </div>
    </div>
  )
}

function EmployeeDetails({
  emp,
  days,
  setDays,
}: {
  emp: Employee | null
  days: number
  setDays: (days: number) => void
}) {
  const { data: log = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['screen-log', emp?.id, days],
    queryFn: () => fetchUserScreenLog(emp!.id, days, 500),
    enabled: !!emp,
    staleTime: 60 * 1000,
  })

  if (!emp) {
    return (
      <div className="flex h-full min-h-[360px] items-center justify-center rounded-md border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        Выберите пользователя из списка, чтобы увидеть поминутный журнал экранов.
      </div>
    )
  }

  const totalDuration = log.reduce((sum, item) => sum + (item.duration_sec ?? 0), 0)

  return (
    <div className="flex min-h-[520px] flex-col rounded-md border border-border bg-card">
      <div className="border-b border-border p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-slate-500" />
              <h2 className="truncate text-base font-semibold text-foreground">{emp.name}</h2>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {emp.username ? `@${emp.username}` : emp.email ?? 'Контакт не указан'}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded border border-border px-2 py-0.5">{ROLE_LABELS[emp.role]}</span>
              <span className="rounded border border-border px-2 py-0.5">{GROUP_LABELS[emp.roleGroup]}</span>
              <span className="rounded border border-border px-2 py-0.5">{emp.deviceType ?? 'Устройство неизвестно'}</span>
              <span className="rounded border border-border px-2 py-0.5">Версия {emp.appVersion ?? 'неизвестна'}</span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {[7, 14, 30].map(dayCount => (
              <button
                key={dayCount}
                type="button"
                onClick={() => setDays(dayCount)}
                className={`h-8 rounded-md border px-2.5 text-xs font-medium ${
                  days === dayCount
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {dayCount} дн.
              </button>
            ))}
            <button
              type="button"
              onClick={() => refetch()}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-muted"
              title="Обновить"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <div className="rounded-md border border-border bg-slate-50 p-2">
            <div className="text-[11px] text-muted-foreground">Последний вход</div>
            <div className="mt-1 font-semibold">{formatDateTime(emp.lastSeenAt)}</div>
          </div>
          <div className="rounded-md border border-border bg-slate-50 p-2">
            <div className="text-[11px] text-muted-foreground">Событий</div>
            <div className="mt-1 font-semibold">{log.length}</div>
          </div>
          <div className="rounded-md border border-border bg-slate-50 p-2">
            <div className="text-[11px] text-muted-foreground">Время</div>
            <div className="mt-1 font-semibold">{formatDuration(totalDuration)}</div>
          </div>
          {emp.workedAsCount > 0 ? (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-2">
              <div className="text-[11px] text-sky-700">Работал под чужими учётками</div>
              <div className="mt-1 font-semibold text-sky-800">
                {emp.workedAsCount} {pluralizeAccounts(emp.workedAsCount)} · {formatHours(emp.workedAsHours)}
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-border bg-slate-50 p-2">
              <div className="text-[11px] text-muted-foreground">Локальные сессии</div>
              <div className="mt-1 font-semibold">
                {emp.devSessions > 0 ? `${emp.devSessions} · ${formatHours(emp.devHours)}` : '—'}
              </div>
            </div>
          )}
        </div>

        {emp.devSessions > 0 && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            {formatHours(emp.devHours)} в {emp.devSessions} сессиях пришли из локальной сборки без имени
            исполнителя (работа разработчика под этой учёткой). В часы и сессии активности они не входят.
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="p-4 text-sm text-red-600">
            Не удалось загрузить журнал экранов.
          </div>
        )}
        {isLoading && (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4, 5].map(i => <RowSkeleton key={i} />)}
          </div>
        )}
        {!isLoading && !error && log.length === 0 && (
          <div className="flex min-h-[280px] items-center justify-center p-8 text-center text-sm text-muted-foreground">
            За выбранный период нет записей. Новые события начнут показывать адрес и детализацию после обновления трекера.
          </div>
        )}
        {!isLoading && !error && log.map((entry, index) => (
          <ActivityEventRow key={`${entry.created_at}-${index}`} entry={entry} />
        ))}
      </div>
    </div>
  )
}

export function EmployeeActivityDashboard() {
  const navigate = useNavigate()
  const user = useAuthStore(s => s.user)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filterRole, setFilterRole] = useState<FilterRole>('all')
  const [devOnly, setDevOnly] = useState(false)
  const [impersonatedOnly, setImpersonatedOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [days, setDays] = useState(14)

  const { data: activityStats = [], isLoading, error } = useQuery({
    queryKey: ['all-users-activity-stats'],
    queryFn: fetchTeamActivityStats,
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  })

  const employees = useMemo(() => {
    return (activityStats as ActivityStats[])
      .map(buildEmployee)
      .sort((a, b) => {
        const aTime = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
        const bTime = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
        if (aTime !== bTime) return bTime - aTime
        return a.name.localeCompare(b.name, 'ru')
      })
  }, [activityStats])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return employees.filter(emp => {
      if (filterRole !== 'all' && emp.role !== filterRole) return false
      // Разработчиком считаем и участника команды, и того, кто работал под
      // чужой учёткой: до применения миграции второго признака ещё нет, а после
      // он и есть ответ на вопрос «кто заходил через мок».
      if (devOnly && !emp.isDeveloper && emp.workedAsCount === 0) return false
      if (impersonatedOnly && emp.devSessions === 0) return false
      if (!term) return true
      return [emp.name, emp.username, emp.email ?? '', ROLE_LABELS[emp.role]]
        .join(' ')
        .toLowerCase()
        .includes(term)
    })
  }, [employees, filterRole, devOnly, impersonatedOnly, search])

  useEffect(() => {
    if (selectedId && filtered.some(emp => emp.id === selectedId)) return
    setSelectedId(filtered[0]?.id ?? null)
  }, [filtered, selectedId])

  const selectedEmployee = useMemo(
    () => filtered.find(emp => emp.id === selectedId) ?? null,
    [filtered, selectedId]
  )

  if (error) {
    return (
      <div className="p-4 text-center text-red-600">
        Ошибка загрузки активности. Проверьте, что миграция app_events применена.
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="mx-auto max-w-7xl px-4 py-5">
        <button
          type="button"
          onClick={() => navigate('/management/users')}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Пользователи
        </button>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Активность сотрудников</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Список пользователей и подробный журнал экранов с адресом и контекстом сущностей.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{employees.length} пользователей</span>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[420px_minmax(0,1fr)]">
          <div className="rounded-md border border-border bg-card">
            <div className="border-b border-border p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Поиск по имени, username, email"
                  className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-8 text-sm outline-none focus:border-slate-400"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                    title="Очистить поиск"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {ROLE_OPTIONS.map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setFilterRole(role)}
                    className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                      filterRole === role
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {role === 'all' ? 'Все' : ROLE_LABELS[role]}
                  </button>
                ))}
              </div>

              {/* Разработка — не роль в УК, а отдельная ось: участник команды
                  разработки может числиться кем угодно. Поэтому это отдельные
                  переключатели, а не ещё два чипа в списке ролей выше. */}
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setDevOnly(v => !v)}
                  className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                    devOnly
                      ? 'border-sky-500 bg-sky-50 text-sky-700'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  }`}
                  title="Участники команды разработки"
                >
                  Разработка
                </button>
                <button
                  type="button"
                  onClick={() => setImpersonatedOnly(v => !v)}
                  className={`h-8 shrink-0 rounded-md border px-2.5 text-xs font-medium ${
                    impersonatedOnly
                      ? 'border-sky-500 bg-sky-50 text-sky-700'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  }`}
                  title="Учётки, под которыми работали из локальной сборки"
                >
                  Заходили локально
                </button>
              </div>
            </div>

            <div className="max-h-[calc(100vh-230px)] min-h-[360px] overflow-y-auto">
              {isLoading && (
                <div className="space-y-2 p-3">
                  {[0, 1, 2, 3, 4, 5, 6].map(i => <RowSkeleton key={i} />)}
                </div>
              )}
              {!isLoading && filtered.map(emp => (
                <EmployeeRow
                  key={emp.id}
                  emp={emp}
                  selected={emp.id === selectedId}
                  onClick={() => setSelectedId(emp.id)}
                />
              ))}
              {!isLoading && filtered.length === 0 && (
                <div className="flex min-h-[260px] items-center justify-center p-6 text-center text-sm text-muted-foreground">
                  Пользователи по выбранным фильтрам не найдены.
                </div>
              )}
            </div>
          </div>

          <EmployeeDetails emp={selectedEmployee} days={days} setDays={setDays} />
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Smartphone className="h-4 w-4" />
          <span>
            Старые события без обновлённой миграции могут показывать только название экрана без адреса.
          </span>
        </div>
      </div>
    </div>
  )
}
